import React, { useEffect } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Typography,
  Alert,
  useTheme
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, Resolver, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";

import { Order } from "../types/salesOrder";
import { formatOrderStatusLabel, isOrderEditable } from "../utils/orderUtils";
import { useUpdateOrderCustomer } from "../../../hooks/api/useSalesOrder";
import { useGetCustomerList } from "../../../hooks/api/useCustomer";

import { buildLogContext, captureAppException } from "../../../Utils/appLogger";

const customerSchema = Yup.object().shape({
  customerId: Yup.string().optional()
});

type FormValues = {
  customerId?: string;
};

interface EditSalesOrderModalProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  salesOrder: Order;
}

const EditSalesOrderModal: React.FC<EditSalesOrderModalProps> = ({
  open,
  onClose,
  onSaved,
  salesOrder
}) => {
  const { t } = useKivunovaTranslation();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));

  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const userCompanyId = userDetails?.companyId ?? null;
  const userId = userDetails?.id;
  const storeId = userDetails?.storeId;

  const updateOrderCustomer = useUpdateOrderCustomer();

  const { data: customerList = [], isLoading: fetchingCustomers } =
    useGetCustomerList(userCompanyId ?? undefined);

  const editable = isOrderEditable(salesOrder.status);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<FormValues>({
    resolver: yupResolver(customerSchema) as Resolver<FormValues>,
    defaultValues: {
      customerId: salesOrder.customerId ?? ""
    }
  });

  useEffect(() => {
    if (open && salesOrder) {
      reset({ customerId: salesOrder.customerId ?? "" });
    }
  }, [open, salesOrder, reset]);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      await updateOrderCustomer.mutateAsync({
        orderId: salesOrder.orderId,
        customerId: data.customerId?.trim() || null
      });
      onSaved?.();
      onClose();
    } catch (error) {
      captureAppException(
        "Failed to update order customer from modal",
        error,
        buildLogContext(
          "orders",
          "update-order-customer-modal",
          userId,
          userCompanyId ?? undefined,
          storeId ?? undefined,
          { orderId: salesOrder.orderId }
        )
      );
    }
  };

  const isLoading = isSubmitting || updateOrderCustomer.isPending;

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="modal-styles">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: matchMD ? 2 : 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0
          }}
        >
          <Typography
            variant={matchMD ? "h6" : "h5"}
            fontWeight="bold"
            color="textPrimary"
          >
            {t("edit-order-title", "Edit Order")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: matchMD ? 2 : 3,
            pt: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
              borderRadius: "10px"
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "10px",
              "&:hover": { backgroundColor: "#555" }
            }
          }}
        >
          {/* Order info (read-only) */}
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              {t("order-number", "Order Number")}
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {salesOrder.orderNumber}
            </Typography>
          </Box>

          {/* Status badge */}
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              {t("order-status", "Status")}
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {formatOrderStatusLabel(salesOrder.status, t)}
            </Typography>
          </Box>

          {/* Non-editable warning */}
          {!editable && (
            <Alert severity="warning">
              {t(
                "edit-order-not-editable",
                "This order can no longer be edited because it has been paid, completed, or cancelled."
              )}
            </Alert>
          )}

          {/* Customer dropdown */}
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Controller
              name="customerId"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  error={!!errors.customerId}
                  disabled={isLoading || fetchingCustomers || !editable}
                >
                  <InputLabel id="customer-select-label">
                    {t("edit-order-customer-label", "Customer (optional)")}
                  </InputLabel>

                  {fetchingCustomers ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: matchMD ? 1 : 1.75,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        mt: "1px"
                      }}
                    >
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        {t(
                          "add-sales-order-loading-customers",
                          "Loading customers..."
                        )}
                      </Typography>
                    </Box>
                  ) : (
                    <Select
                      {...field}
                      labelId="customer-select-label"
                      label={t(
                        "edit-order-customer-label",
                        "Customer (optional)"
                      )}
                    >
                      <MenuItem value="">
                        <em>{t("add-sales-order-no-customer", "None")}</em>
                      </MenuItem>

                      {customerList.length === 0 ? (
                        <MenuItem disabled value="">
                          {t(
                            "add-sales-order-empty-customers",
                            "No customers found"
                          )}
                        </MenuItem>
                      ) : (
                        customerList.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                            {c.phone ? ` — ${c.phone}` : ""}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  )}

                  {errors.customerId && (
                    <FormHelperText>{errors.customerId.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Box>

          {/* Info note */}
          <Box
            sx={{
              p: 2,
              bgcolor: "info.light",
              borderRadius: 1
            }}
          >
            <Typography variant="body2" color="info.dark">
              {t(
                "edit-order-items-info",
                "To add, remove, or update order items, use the scan flow on the Order Detail page."
              )}
            </Typography>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            flexDirection: matchMD ? "column" : "row",
            gap: matchMD ? 1 : 2,
            p: matchMD ? 2 : 3,
            pt: matchMD ? 1.5 : 2,
            borderTop: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
            backgroundColor: "background.paper"
          }}
        >
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isLoading}
            disabled={!editable || fetchingCustomers}
            fullWidth
            size={matchMD ? "medium" : "large"}
            onClick={handleSubmit(onSubmit)}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: BRAND_COLOR,
              "&:hover": { backgroundColor: BRAND_HOVER }
            }}
          >
            {t("save-changes", "Save Changes")}
          </LoadingButton>

          <LoadingButton
            type="button"
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
            fullWidth
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: "white",
              color: BRAND_COLOR,
              border: `1px solid ${BRAND_COLOR}`,
              "&:hover": {
                backgroundColor: BRAND_HOVER,
                color: "white",
                borderColor: BRAND_HOVER
              }
            }}
          >
            {t("cancel", "Cancel")}
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditSalesOrderModal;
